import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useGetRemitAccuracyRowsQuery } from "store/queries/remitAccuracy";
import {
  IRemitAccuracyFilters,
  IRemitAccuracyRow,
  RemitAiAgreement,
  RemitScoredBenefit,
} from "types/RemitAccuracy/RemitAccuracy";

import {
  AI_AGREEMENT_LABELS,
  formatConfidence,
  formatRemitValue,
  REMIT_BENEFIT_LABELS,
} from "./remitAccuracyWording";

const PAGE_SIZE = 25;

const checkBody = (row: IRemitAccuracyRow) => (
  <div className="flex flex-column gap-1">
    <span>RequestID {row.pverifyRequestId ?? "—"}</span>
    <span className="text-xs text-color-secondary">
      {row.pverifyResponseId}
    </span>
  </div>
);

const orderBody = (row: IRemitAccuracyRow) =>
  row.orderIds[0] ? (
    <Link
      to={`/orders/${row.orderIds[0]}`}
      target="_blank"
      rel="noopener noreferrer"
    >
      Estimate
    </Link>
  ) : (
    "—"
  );

/** CLE-2820 — the checks behind one four-way count on a tile: the engine (today's config) and the AI, side by side. */
export function RemitAccuracyAiChecksDialog({
  selection,
  filters,
  onHide,
}: {
  selection: {
    benefit: RemitScoredBenefit;
    agreement: RemitAiAgreement;
  } | null;
  filters: IRemitAccuracyFilters;
  onHide: () => void;
}) {
  const [offset, setOffset] = useState(0);
  const { data, isFetching } = useGetRemitAccuracyRowsQuery(
    {
      ...filters,
      benefit: selection?.benefit,
      includeAi: true,
      aiAgreement: selection?.agreement ?? "BOTH_RIGHT",
      limit: PAGE_SIZE,
      offset,
    },
    { skip: !selection }
  );
  const page = data?.data;
  const value = (amount: number | null | undefined) =>
    selection ? formatRemitValue(amount, selection.benefit) : "—";

  return (
    <Dialog
      header={
        selection
          ? `${REMIT_BENEFIT_LABELS[selection.benefit]} · ${
              AI_AGREEMENT_LABELS[selection.agreement]
            }`
          : ""
      }
      visible={Boolean(selection)}
      style={{ width: "min(1200px, 95vw)" }}
      onHide={() => {
        setOffset(0);
        onHide();
      }}
    >
      <DataTable
        value={page?.rows ?? []}
        lazy
        paginator
        rows={PAGE_SIZE}
        first={offset}
        totalRecords={page?.total ?? 0}
        onPage={(event) => setOffset(event.first)}
        loading={isFetching}
        emptyMessage="No checks."
        data-testid="remitAccuracy.aiChecks"
      >
        <Column field="accountName" header="Account" />
        <Column
          header="Payer · Plan"
          body={(row: IRemitAccuracyRow) =>
            `${row.payerName ?? "No payer"} · ${row.planName ?? "No plan name"}`
          }
        />
        <Column header="Check" body={checkBody} />
        <Column
          header="Engine"
          body={(row: IRemitAccuracyRow) => value(row.engineValue)}
        />
        <Column
          header="AI"
          body={(row: IRemitAccuracyRow) =>
            `${value(row.aiValue)} · ${formatConfidence(row.aiConfidence)}`
          }
        />
        <Column
          header="Remit"
          body={(row: IRemitAccuracyRow) => value(row.remitValue)}
        />
        <Column header="Estimate" body={orderBody} />
      </DataTable>
    </Dialog>
  );
}
